import React, { useState } from 'react';
import CosmicIcon from '../../common/CosmicIcon';
import { COSMIC_JOURNEYS } from './cosmicJourneys';
import './CosmicCareerDirections.css';

/**
 * Cosmic Career Directions — in-app journey maps (2026-06-27)
 *
 * Renders the APC Career Explorer + Career Pivot coaching journeys directly in
 * the Careers tab, cosmic-themed, replacing the old link-out to the GitHub
 * Pages journey site. Content is vendored in cosmicJourneys.js (source of truth:
 * bookbran/apc-journeys-data). The full Premium "Complete Universe" careers UI
 * is preserved in CosmicCareerDirections.archive.js for the future industry +
 * role-type module. Presentation/content only; no engine, scoring, or item-bank
 * logic here.
 */

const SCHEDULE_URL = 'https://aportlandcareer.com/schedule';

const CosmicCareerDirections = ({ results }) => {
  const constellationName = results?.constellationData?.name;
  const [activeKey, setActiveKey] = useState(COSMIC_JOURNEYS[0].key);
  const journey =
    COSMIC_JOURNEYS.find((j) => j.key === activeKey) || COSMIC_JOURNEYS[0];

  return (
    <div className="cosmic-career-directions cci ccj">

      {/* Honest framing header */}
      <div className="cosmic-card cosmic-card--professional cci-intro">
        <div className="cosmic-card__header">
          <p className="cci-eyebrow">
            <CosmicIcon name="results_careers" size={15} decorative /> Career Directions
          </p>
          <h2 className="cosmic-card__title">Your map, and the path to walking it</h2>
          <p className="cosmic-card__subtitle">
            You finished the assessment, so the hardest piece is already yours, a clear picture of
            what pulls you toward your best work
            {constellationName ? <> as a {constellationName}</> : null}. The layer that lines your
            motivated elements up against specific industries and roles is something we are still
            building. In the meantime, here is exactly how A Portland Career turns a constellation
            like yours into a plan, the same two journeys our coaches walk every week.
          </p>
        </div>
      </div>

      {/* Journey switch */}
      <div className="ccj-switch" role="tablist" aria-label="Coaching journeys">
        {COSMIC_JOURNEYS.map((j) => {
          const isActive = j.key === activeKey;
          return (
            <button
              key={j.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`ccj-switch-btn${isActive ? ' is-active' : ''}`}
              onClick={() => setActiveKey(j.key)}
            >
              <CosmicIcon name={j.icon} size={18} decorative />
              <span>{j.name}</span>
            </button>
          );
        })}
      </div>

      {/* Active journey */}
      <div className="ccj-journey" key={journey.key}>
        <div className="ccj-journey-head">
          <h3 className="ccj-journey-name">{journey.name}</h3>
          <p className="ccj-journey-tagline">{journey.tagline}</p>
          <p className="ccj-journey-desc">{journey.description}</p>
          <div className="ccj-journey-meta">
            <span className="ccj-pill ccj-pill--count">
              <CosmicIcon name="ui_clock" size={13} decorative />
              {journey.sessionCount}
            </span>
            {journey.bestFor.map((b) => (
              <span key={b} className="ccj-pill">{b}</span>
            ))}
          </div>
        </div>

        {/* The constellation path */}
        <div className="ccj-path">
          {journey.phases.map((phase) => (
            <React.Fragment key={phase.id}>
              <section
                className="ccj-phase"
                style={{ '--accent': phase.accent }}
              >
                <div className="ccj-phase-head">
                  <span className="ccj-phase-node">{phase.id}</span>
                  <div className="ccj-phase-titles">
                    <h4 className="ccj-phase-name">{phase.name}</h4>
                    <p className="ccj-phase-sub">{phase.subtitle}</p>
                  </div>
                </div>

                <div className="ccj-sessions">
                  {phase.sessions.map((session) => (
                    <article key={session.id} className="ccj-session">
                      <p className="ccj-session-eyebrow">Session {session.id}</p>
                      <h5 className="ccj-session-title">{session.title}</h5>
                      <ul className="ccj-acts">
                        {session.activities.map((act, i) => (
                          <li key={i} className="ccj-act">
                            <CosmicIcon name="ui_check" size={15} decorative />
                            <span>{act}</span>
                          </li>
                        ))}
                      </ul>
                      {session.homework ? (
                        <p className="ccj-homework">
                          <span className="ccj-homework-label">Between sessions</span>
                          {session.homework}
                        </p>
                      ) : null}
                    </article>
                  ))}
                </div>
              </section>

              {/* Parallel materials support (Pivot), woven in after its phase */}
              {journey.parallelSupport &&
              journey.parallelSupport.afterPhaseId === phase.id ? (
                <aside className="ccj-parallel">
                  <div className="ccj-parallel-head">
                    <CosmicIcon name="ui_list" size={18} decorative />
                    <h4 className="ccj-parallel-title">
                      {journey.parallelSupport.title}
                    </h4>
                  </div>
                  <p className="ccj-parallel-note">{journey.parallelSupport.note}</p>
                  <div className="ccj-parallel-items">
                    {journey.parallelSupport.items.map((item) => (
                      <span key={item} className="ccj-parallel-item">{item}</span>
                    ))}
                  </div>
                </aside>
              ) : null}
            </React.Fragment>
          ))}

          {/* Destination */}
          <section className="ccj-destination">
            <span className="ccj-destination-star">
              <CosmicIcon name="brand_spark" size={26} decorative />
            </span>
            <div className="ccj-destination-text">
              <h4 className="ccj-destination-title">{journey.destination.title}</h4>
              <p className="ccj-destination-note">{journey.destination.note}</p>
            </div>
          </section>
        </div>

        <p className="ccj-coaches">
          <CosmicIcon name="nav_coach" size={14} decorative />
          <span>Often guided by {journey.recommendedCoaches}</span>
        </p>
      </div>

      {/* Coaching handoff + primary CTA */}
      <div className="cosmic-card cosmic-card--professional cci-cta-card">
        <div className="cci-cta-inner">
          <h3 className="cci-cta-title">Start with a conversation</h3>
          <p className="cci-cta-sub">
            These journeys move with a coach beside you, reading the same language your results are
            built on. An intro call is free, and it is the cleanest way to go from your map to your
            first real step.
          </p>
          <a
            className="cosmic-cta cosmic-cta--full cci-cta-button"
            href={SCHEDULE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="cosmic-cta-icon">
              <CosmicIcon name="nav_coach" size={18} decorative />
            </span>
            Book an intro call
          </a>
        </div>
      </div>

    </div>
  );
};

export default CosmicCareerDirections;
